import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PrivateRoute from "components/Routes/ProtectedRoute";
import Layout from "components/Layouts";
import PageLoader from "components/PageLoader";

import { useAuthSlice } from "store/auth/slice";
import { selectUser } from "store/auth/slice/selector";

import Login from "pages/Login";
import TalentUploadRequest from "pages/TalentUploadRequest";
import Companies from "pages/Companies";
import RosterStatusPage from "pages/RosterStatusPage";
import MarketersPage from "pages/MarketerColumn";
import ListDomains from "pages/ListDomains";
import TalentAgencies from "pages/TalentAgencies/index";
import AgencyPage from "pages/TalentAgencies/AgencyPage/index";
import Marketplace from "pages/Marketplace/index";
import UsersManagement from "pages/Users/index";
import UserProfileInfo from "pages/Users/UserProfileInfo/index";
import SearchHistory from "pages/Users/SearchHistory";
import CreatorsVerification from "pages/CreatorsVerification/index";
// Creators Page
import CreatorsPage from "pages/Creators/index";
import AddCreatorPage from "pages/Creators/AddCreator/index";
import EditCreatorPage from "pages/Creators/EditCreator/index";
import BulkImportCreatorsPage from "pages/Creators/BulkImportCreators/index";
import CampaignWrapper from "pages/Marketplace/Campaign/index";

// creator users
import CreatorUsersPage from "pages/CreatorUsers/index";
import CreatorUserProfileInfo from "pages/CreatorUsers/UsersProfileInfo/index";
import UserInsights from "pages/Users/UserInsights/index";
import CampaignReport from "pages/Marketplace/CampaignReport";
import BrandFollowers from "pages/CreatorUsers/BrandFollowers";
import MarketplaceApproval from "pages/Marketplace/CampaignApproval";
import UserApproval from "pages/EmailApproval/UserApproval";
import CreatorSubscriptionDetails from "pages/CreatorUsers/CreatorSubscriptionDetails";

/**
 * Helper component to handle redirect + cleanup after login
 */
const RedirectHandler = ({ redirectPath }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (redirectPath) {
      // 1️Navigate programmatically
      navigate(redirectPath, { replace: true });
      // 2️Remove the redirect key immediately after navigation
      sessionStorage.removeItem("redirectAfterLogin");
    }
  }, [navigate, redirectPath]);

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const { actions } = useAuthSlice();
  const { user, loading } = useSelector(selectUser);
  const location = useLocation();

  // Handle OAuth redirect in dev
  const currentPath = window.location.pathname;
  if (currentPath.startsWith("/api/auth")) {
    window.location.href = `http://localhost:3003${currentPath}${window.location.search}`;
    return null;
  }

  // Check authentication state on app mount
  useEffect(() => {
    dispatch(actions.checkAuthRequest());
  }, [dispatch, actions]);

  // Determine what to render at /login
  const renderLoginRoute = () => {
    if (loading) {
      return <PageLoader fullscreen />;
    }

    if (user) {
      const savedRedirect = sessionStorage.getItem("redirectAfterLogin");

      return <RedirectHandler redirectPath={savedRedirect || "/companies"} />;
    }

    return <Login />;
  };
  return (
    <Routes>
      {/* Login route */}
      <Route path="/login" element={renderLoginRoute()} />

      <Route
        path="/marketplace-approval"
        element={
          <PrivateRoute>
            <MarketplaceApproval />
          </PrivateRoute>
        }
      />

      <Route
        path="/user-activation"
        element={
          <PrivateRoute>
            <UserApproval />
          </PrivateRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route path="companies" element={<Companies />} />
        <Route path="talent-agencies" element={<TalentAgencies />} />
        <Route path="talent-agencies/:agencyId" element={<AgencyPage />} />
        <Route path="talent-upload-request" element={<TalentUploadRequest />} />
        <Route path="marketplace" element={<Marketplace />} />
        <Route
          path="marketplace/create-campaign"
          element={<CampaignWrapper mode="create" />}
        />
        <Route
          path="marketplace/edit-campaign/:campaignId"
          element={<CampaignWrapper mode="edit" />}
        />
        <Route
          path="companies/:companyId/marketers"
          element={<MarketersPage />}
        />
        <Route path="companies/:companyId" element={<RosterStatusPage />} />
        <Route path="companies/:companyId/domains" element={<ListDomains />} />

        <Route path="marketer-users" element={<UsersManagement />} />
        <Route path="marketer-search-history" element={<SearchHistory />} />
        <Route path="marketer-users/:userId" element={<UserProfileInfo />} />
        <Route
          path="marketer-users/user-insights/:userId"
          element={<UserInsights />}
        />
        <Route path="campaign-report" element={<CampaignReport />} />

        <Route path="creators" element={<CreatorsPage />} />
        {/* <Route path="creators/add" element={<AddCreatorPage />} /> */}
        <Route path="creators/edit/:creatorId" element={<EditCreatorPage />} />
        {/* <Route
          path="creators/bulk-import"
          element={<BulkImportCreatorsPage />}
        /> */}

        <Route path="creator-users" element={<CreatorUsersPage />} />
        <Route
          path="creator-users/:userId"
          element={<CreatorUserProfileInfo />}
        />

        <Route
          path="creators-verification"
          element={<CreatorsVerification />}
        />

        <Route path="brand-followers" element={<BrandFollowers />} />

        <Route
          path="creator-users/:id/subscription/:subscriptionId"
          element={<CreatorSubscriptionDetails />}
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Route>
    </Routes>
  );
};

export default App;
